import React, { ChangeEvent, DragEvent, PointerEvent, WheelEvent, useEffect, useRef, useState } from 'react';
import { FaArrowsAlt, FaCube, FaFileAlt, FaMobileAlt, FaMoon, FaMousePointer, FaRedoAlt, FaSun, FaSyncAlt, FaUpload } from 'react-icons/fa';
import SEO from '../components/SEO';

const MODEL_VIEWER_SCRIPT_ID = 'redrender-model-viewer';
const MODEL_VIEWER_SRC = 'https://unpkg.com/@google/model-viewer/dist/model-viewer.min.js';

interface ModelSource {
  url: string;
  name: string;
  sizeLabel: string;
  sourceLabel: string;
  isUploaded: boolean;
}

type ModelViewerElement = HTMLElement & {
  activateAR?: () => Promise<void> | void;
  loaded?: boolean;
};

type TransformMode = 'move' | 'rotate' | 'scale';
type ViewerMode = 'viewer' | 'webar';

interface GesturePoint {
  x: number;
  y: number;
}

interface GestureSnapshot {
  mode: TransformMode | 'pinch';
  startX: number;
  startY: number;
  startMoveX: number;
  startMoveY: number;
  startRotation: number;
  startScale: number;
  startDistance?: number;
}

const defaultModel: ModelSource = {
  url: '/models/chair.glb',
  name: 'Chair.glb',
  sizeLabel: '7.2 MB',
  sourceLabel: 'Default model',
  isUploaded: false,
};

const DEFAULT_MODEL_SCALE = 1;
const BASE_CAMERA_DISTANCE = 3.35;
const TOUCH_MOVE_LIMIT_X_RATIO = 0.46;
const TOUCH_MOVE_LIMIT_Y_RATIO = 0.38;

const formatFileSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const normalizeDegrees = (value: number) => Math.round(((value % 360) + 360) % 360);
const getDistance = (first: GesturePoint, second: GesturePoint) => (
  Math.hypot(second.x - first.x, second.y - first.y)
);

const WebAR: React.FC = () => {
  const [model, setModel] = useState<ModelSource>(defaultModel);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerStatus, setViewerStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [isModelReady, setIsModelReady] = useState(false);
  const [lightingMode, setLightingMode] = useState<'day' | 'night'>('day');
  const [moveX, setMoveX] = useState(0);
  const [moveY, setMoveY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [modelScale, setModelScale] = useState(DEFAULT_MODEL_SCALE);
  const [sunRotation, setSunRotation] = useState(40);
  const [activeTransformMode, setActiveTransformMode] = useState<TransformMode>('move');
  const [viewerMode, setViewerMode] = useState<ViewerMode>('viewer');
  const uploadedUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modelViewerRef = useRef<ModelViewerElement | null>(null);
  const activePointersRef = useRef<Map<number, GesturePoint>>(new Map());
  const gestureSnapshotRef = useRef<GestureSnapshot | null>(null);
  const transformValuesRef = useRef({
    moveX: 0,
    moveY: 0,
    rotation: 0,
    scale: DEFAULT_MODEL_SCALE,
  });

  const isNightMode = lightingMode === 'night';
  const viewerExposure = isNightMode ? 0.62 : 1.18;
  const viewerShadow = isNightMode ? 1.15 : 0.85;
  const cameraDistance = (BASE_CAMERA_DISTANCE / modelScale).toFixed(2);
  const cameraOrbit = `${rotation}deg 70deg ${cameraDistance}m`;
  const viewerTransform = viewerMode === 'webar' ? `translate(-50%, -50%) translate3d(${moveX}px, ${moveY}px, 0)` : 'translate(-50%, -50%)';
  const sunRadians = (sunRotation * Math.PI) / 180;
  const sunX = Math.round(50 + Math.cos(sunRadians) * 34);
  const sunY = Math.round(50 + Math.sin(sunRadians) * 28);
  const sunGlowStyle = {
    background: isNightMode
      ? `radial-gradient(circle at ${sunX}% ${sunY}%, rgba(125, 211, 252, 0.18), transparent 18rem), radial-gradient(circle at 50% 80%, rgba(255,85,85,0.10), transparent 26rem)`
      : `radial-gradient(circle at ${sunX}% ${sunY}%, rgba(255, 244, 180, 0.24), transparent 18rem), radial-gradient(circle at 50% 82%, rgba(255,85,85,0.12), transparent 24rem)`,
  };

  const resetControls = () => {
    setLightingMode('day');
    setSunRotation(40);
    setMoveX(0);
    setMoveY(0);
    setRotation(0);
    setModelScale(DEFAULT_MODEL_SCALE);
  };

  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) {
      setViewerMode('webar');
    }
  }, []);

  useEffect(() => {
    transformValuesRef.current = {
      moveX,
      moveY,
      rotation,
      scale: modelScale,
    };
  }, [modelScale, moveX, moveY, rotation]);

  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      if (!cancelled) {
        setViewerStatus('ready');
      }
    };

    const markError = () => {
      if (!cancelled) {
        setViewerStatus('error');
      }
    };

    if (!('customElements' in window)) {
      markError();
      return undefined;
    }

    if (window.customElements.get('model-viewer')) {
      markReady();
      return undefined;
    }

    let script = document.getElementById(MODEL_VIEWER_SCRIPT_ID) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = MODEL_VIEWER_SCRIPT_ID;
      script.type = 'module';
      script.src = MODEL_VIEWER_SRC;
      document.head.appendChild(script);
    }

    script.addEventListener('error', markError);
    window.customElements.whenDefined('model-viewer').then(markReady).catch(markError);

    return () => {
      cancelled = true;
      script?.removeEventListener('error', markError);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (uploadedUrlRef.current) {
        URL.revokeObjectURL(uploadedUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const viewer = modelViewerRef.current;

    if (!viewer) {
      return undefined;
    }

    setIsModelReady(Boolean(viewer.loaded));

    const handleModelLoad = () => {
      setIsModelReady(true);
      setError(null);
    };

    const handleModelError = () => {
      setIsModelReady(false);
      setError('Model could not load. Please try another GLB or GLTF file.');
    };

    viewer.addEventListener('load', handleModelLoad);
    viewer.addEventListener('error', handleModelError);

    return () => {
      viewer.removeEventListener('load', handleModelLoad);
      viewer.removeEventListener('error', handleModelError);
    };
  }, [model.url, viewerStatus]);

  const releaseUploadedModel = () => {
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
  };

  const loadModelFile = (file?: File) => {
    if (!file) {
      return;
    }

    if (!/\.(glb|gltf)$/i.test(file.name)) {
      setError('Please upload a GLB or GLTF model.');
      return;
    }

    releaseUploadedModel();
    const objectUrl = URL.createObjectURL(file);
    uploadedUrlRef.current = objectUrl;
    setModel({
      url: objectUrl,
      name: file.name,
      sizeLabel: formatFileSize(file.size),
      sourceLabel: 'Uploaded model',
      isUploaded: true,
    });
    setIsModelReady(false);
    setError(null);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    loadModelFile(event.target.files?.[0]);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
    loadModelFile(event.dataTransfer.files?.[0]);
  };

  const resetToDefault = () => {
    releaseUploadedModel();
    setModel(defaultModel);
    setError(null);
    setIsModelReady(false);
    resetControls();

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const beginGesture = (points: GesturePoint[], pointer?: GesturePoint, mode: TransformMode = activeTransformMode) => {
    const currentValues = transformValuesRef.current;

    if (points.length >= 2) {
      gestureSnapshotRef.current = {
        mode: 'pinch',
        startX: 0,
        startY: 0,
        startMoveX: currentValues.moveX,
        startMoveY: currentValues.moveY,
        startRotation: currentValues.rotation,
        startScale: currentValues.scale,
        startDistance: getDistance(points[0], points[1]),
      };
      return;
    }

    if (!pointer) {
      gestureSnapshotRef.current = null;
      return;
    }

    gestureSnapshotRef.current = {
      mode,
      startX: pointer.x,
      startY: pointer.y,
      startMoveX: currentValues.moveX,
      startMoveY: currentValues.moveY,
      startRotation: currentValues.rotation,
      startScale: currentValues.scale,
    };
  };

  const handleTransformPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!isModelReady) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    const pointers = activePointersRef.current;
    pointers.set(event.pointerId, point);
    const desktopMode: TransformMode = event.button === 1 || event.button === 2 || event.shiftKey ? 'move' : 'rotate';
    beginGesture(Array.from(pointers.values()), point, viewerMode === 'viewer' ? desktopMode : activeTransformMode);
  };

  const handleTransformPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isModelReady || !activePointersRef.current.has(event.pointerId)) {
      return;
    }

    event.preventDefault();
    const pointers = activePointersRef.current;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const snapshot = gestureSnapshotRef.current;

    if (!snapshot) {
      return;
    }

    const points = Array.from(pointers.values());

    if (points.length >= 2) {
      const distance = getDistance(points[0], points[1]);
      const startDistance = snapshot.startDistance || distance;
      const nextScale = clamp(snapshot.startScale * (distance / startDistance), 0.35, 2.5);
      setModelScale(Number(nextScale.toFixed(2)));
      return;
    }

    const deltaX = event.clientX - snapshot.startX;
    const deltaY = event.clientY - snapshot.startY;

    if (snapshot.mode === 'move') {
      const bounds = event.currentTarget.getBoundingClientRect();
      const maxMoveX = Math.max(bounds.width * TOUCH_MOVE_LIMIT_X_RATIO, 160);
      const maxMoveY = Math.max(bounds.height * TOUCH_MOVE_LIMIT_Y_RATIO, 180);

      setMoveX(Math.round(clamp(snapshot.startMoveX + deltaX, -maxMoveX, maxMoveX)));
      setMoveY(Math.round(clamp(snapshot.startMoveY + deltaY, -maxMoveY, maxMoveY)));
      return;
    }

    if (snapshot.mode === 'rotate') {
      setRotation(normalizeDegrees(snapshot.startRotation + deltaX * 0.7));
      return;
    }

    if (snapshot.mode === 'scale') {
      const nextScale = clamp(snapshot.startScale - deltaY / 220, 0.35, 2.5);
      setModelScale(Number(nextScale.toFixed(2)));
    }
  };

  const endTransformPointer = (event: PointerEvent<HTMLDivElement>) => {
    const pointers = activePointersRef.current;
    pointers.delete(event.pointerId);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    const remainingPoints = Array.from(pointers.values());
    beginGesture(remainingPoints, remainingPoints[0]);
  };

  const handleViewerWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!isModelReady || viewerMode !== 'viewer') {
      return;
    }

    event.preventDefault();
    const nextScale = clamp(transformValuesRef.current.scale - event.deltaY / 700, 0.35, 2.5);
    setModelScale(Number(nextScale.toFixed(2)));
  };

  return (
    <>
      <SEO
        title="Web AR Model Viewer - RedRender"
        description="Preview GLB and GLTF 3D models in the browser and launch supported Web AR experiences with RedRender."
        path="/web-ar"
        keywords={['Web AR', 'GLB viewer', '3D model upload', 'RedRender AR', 'augmented reality']}
      />

      <section className="min-h-screen bg-[#050505] pt-20 text-white">
        <div className="mx-auto max-w-[1600px] px-2 py-3 sm:px-4 lg:px-6">
          <div
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`relative min-h-[calc(100vh-6.5rem)] overflow-hidden rounded-lg border transition-colors duration-500 ${
              isNightMode ? 'bg-[#030611]' : 'bg-[#111]'
            } ${
              isDragging ? 'border-red-300 shadow-2xl shadow-red-500/20' : 'border-white/10'
            }`}
          >
            <div className={`absolute inset-0 transition-opacity duration-500 ${
              isNightMode
                ? 'bg-[radial-gradient(circle_at_20%_16%,rgba(56,189,248,0.16),transparent_24rem),radial-gradient(circle_at_78%_62%,rgba(255,85,85,0.12),transparent_22rem),linear-gradient(rgba(255,255,255,0.028)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.028)_1px,transparent_1px)]'
                : 'bg-[radial-gradient(circle_at_24%_18%,rgba(255,85,85,0.14),transparent_22rem),linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)]'
            } bg-[size:auto,44px_44px,44px_44px]`} />
            <div className="absolute inset-0 transition-opacity duration-500" style={sunGlowStyle} />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 to-transparent" />

            {viewerStatus === 'error' && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/80 px-6 text-center">
                <div>
                  <FaFileAlt className="mx-auto mb-4 text-4xl text-red-300" />
                  <p className="font-semibold text-white">3D viewer could not load.</p>
                  <p className="mt-2 text-sm text-gray-400">Check internet access for the model-viewer script.</p>
                </div>
              </div>
            )}

            {viewerStatus === 'loading' && (
              <div className="absolute left-4 top-4 z-10 rounded-full border border-white/10 bg-black/50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-300">
                Loading viewer
              </div>
            )}

            <div className="absolute left-3 right-3 top-3 z-20 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between lg:left-5 lg:right-5 lg:top-5">
              <div className="max-w-md rounded-lg border border-white/10 bg-black/55 p-3 shadow-2xl shadow-black/30 backdrop-blur-xl">
                <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-100">
                  <FaMobileAlt className="text-red-400" />
                  Web AR
                </div>
                <h1 className="font-display text-xl font-bold leading-tight text-white sm:text-2xl">
                  AR Model Viewer
                </h1>
                <div className="mt-2 flex min-w-0 items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-red-500/15 text-red-300">
                    <FaCube />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{model.name}</p>
                    <p className="text-xs text-gray-400">{model.sourceLabel} - {model.sizeLabel}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-white/10 bg-black/55 p-1.5 backdrop-blur-xl">
                <div className="flex rounded-md border border-white/10 bg-white/5 p-0.5">
                  <button
                    type="button"
                    onClick={() => setViewerMode('viewer')}
                    className={`inline-flex h-8 items-center justify-center gap-1 rounded px-2.5 text-xs font-bold transition ${
                      viewerMode === 'viewer'
                        ? 'bg-white text-black'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <FaMousePointer />
                    3D
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewerMode('webar')}
                    className={`inline-flex h-8 items-center justify-center gap-1 rounded px-2.5 text-xs font-bold transition ${
                      viewerMode === 'webar'
                        ? 'bg-red-500 text-white'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <FaMobileAlt />
                    Adjust
                  </button>
                </div>
                <label
                  htmlFor="model-upload"
                  className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 rounded-md bg-white px-3 text-xs font-bold text-black transition hover:bg-gray-200"
                >
                  <input
                    ref={fileInputRef}
                    id="model-upload"
                    type="file"
                    accept=".glb,.gltf,model/gltf-binary,model/gltf+json"
                    className="sr-only"
                    onChange={handleFileChange}
                  />
                  <FaUpload />
                  Upload
                </label>
                <button
                  type="button"
                  onClick={resetToDefault}
                  disabled={!model.isUploaded}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 text-xs font-semibold text-white transition hover:border-red-400/60 hover:bg-red-500/15 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <FaRedoAlt className="text-xs" />
                  Chair
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const viewer = modelViewerRef.current;

                    if (viewer?.activateAR) {
                      void viewer.activateAR();
                      return;
                    }

                    setError('AR is available on supported Android Chrome or iPhone Safari browsers.');
                  }}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-red-500 px-3 text-xs font-bold text-white shadow-xl shadow-red-500/25 transition hover:bg-red-600"
                >
                  <FaMobileAlt />
                  Open AR
                </button>
              </div>
            </div>

            {error && (
              <div className="absolute left-3 right-3 top-[12.5rem] z-20 rounded-lg border border-red-400/30 bg-red-500/15 px-4 py-3 text-sm font-medium text-red-50 backdrop-blur-xl sm:left-auto sm:max-w-md lg:right-5 lg:top-28">
                {error}
              </div>
            )}

            {isDragging && (
              <div className="absolute inset-3 z-30 flex items-center justify-center rounded-lg border-2 border-dashed border-red-300 bg-black/70 text-center backdrop-blur-sm">
                <div>
                  <FaUpload className="mx-auto mb-4 text-4xl text-red-300" />
                  <p className="text-xl font-bold text-white">Drop GLB or GLTF model</p>
                </div>
              </div>
            )}

            <model-viewer
              ref={modelViewerRef}
              key={model.url}
              src={model.url}
              alt={`${model.name} in Web AR`}
              ar
              ar-modes="webxr scene-viewer quick-look"
              ar-scale="auto"
              camera-controls
              enable-pan
              bounds="tight"
              camera-target="auto auto auto"
              {...(viewerMode === 'webar' ? { 'camera-orbit': cameraOrbit } : {})}
              exposure={viewerExposure}
              environment-image="neutral"
              interaction-prompt="none"
              loading="eager"

              reveal="auto"
              shadow-intensity={viewerShadow}
              touch-action={viewerMode === 'viewer' ? 'pan-y' : 'none'}
              className="absolute top-1/2 left-1/2 z-[1] h-[200%] w-[200%] will-change-transform outline-none"
              style={{ backgroundColor: 'transparent', transform: viewerTransform, transformOrigin: 'center center' }}
            />

            {isModelReady && (
              <div
                aria-label={viewerMode === 'viewer' ? 'Mouse 3D control area' : `Touch ${activeTransformMode} gesture area`}
                data-testid="gesture-layer"
                role="application"
                onContextMenu={(event) => event.preventDefault()}
                onPointerCancel={endTransformPointer}
                onPointerDown={handleTransformPointerDown}
                onPointerLeave={endTransformPointer}
                onPointerMove={handleTransformPointerMove}
                onPointerUp={endTransformPointer}
                onWheel={handleViewerWheel}
                className={`absolute inset-0 z-10 cursor-grab touch-none active:cursor-grabbing ${viewerMode === 'viewer' ? 'pointer-events-none' : ''}`}
              />
            )}

            <div className="absolute left-3 top-[11rem] z-[2] flex flex-wrap gap-2 sm:top-auto sm:bottom-4 lg:left-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-3 py-2 text-xs font-semibold text-gray-200 backdrop-blur-md">
                {viewerMode === 'viewer' ? <FaMousePointer className="text-red-300" /> : <FaArrowsAlt className="text-red-300" />}
                {viewerMode === 'viewer' ? 'Mouse 3D' : `Touch ${activeTransformMode}`}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/60 px-3 py-2 text-xs font-semibold text-gray-200 backdrop-blur-md">
                {viewerMode === 'viewer' ? 'Wheel zoom, right drag pan' : `Pinch scale ${modelScale.toFixed(1)}x`}
              </span>
            </div>

            {isModelReady ? (
              <div className="absolute inset-x-2 bottom-2 z-20 max-h-[42vh] overflow-y-auto rounded-lg border border-white/10 bg-black/72 p-3 shadow-2xl shadow-black/50 backdrop-blur-xl lg:inset-x-5 lg:bottom-5 lg:max-h-none">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/15 text-red-300">
                      <FaSyncAlt />
                    </span>
                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">
                        {viewerMode === 'viewer' ? '3D Viewer' : 'Touch Controls'}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {viewerMode === 'viewer' ? 'Mouse controls active' : 'Model loaded'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setLightingMode('day')}
                      className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition ${
                        lightingMode === 'day'
                          ? 'border-yellow-300/40 bg-yellow-300/15 text-yellow-100'
                          : 'border-white/10 bg-white/5 text-gray-300 hover:border-yellow-300/30 hover:bg-white/10'
                      }`}
                    >
                      <FaSun />
                      Day
                    </button>
                    <button
                      type="button"
                      onClick={() => setLightingMode('night')}
                      className={`inline-flex h-8 items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-semibold transition ${
                        lightingMode === 'night'
                          ? 'border-cyan-300/40 bg-cyan-300/15 text-cyan-100'
                          : 'border-white/10 bg-white/5 text-gray-300 hover:border-cyan-300/30 hover:bg-white/10'
                      }`}
                    >
                      <FaMoon />
                      Night
                    </button>
                    <button
                      type="button"
                      onClick={resetControls}
                      className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 text-xs font-semibold text-white transition hover:border-red-400/60 hover:bg-red-500/15"
                    >
                      <FaRedoAlt className="text-[10px]" />
                      Reset
                    </button>
                  </div>
                </div>

                {viewerMode === 'viewer' ? (
                  <div className="grid gap-2 text-xs text-gray-200 sm:grid-cols-3">
                    <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      <span className="block font-bold text-white">Rotate</span>
                      <span className="text-gray-400">Left mouse drag</span>
                    </div>
                    <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      <span className="block font-bold text-white">Zoom</span>
                      <span className="text-gray-400">Mouse wheel</span>
                    </div>
                    <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                      <span className="block font-bold text-white">Pan</span>
                      <span className="text-gray-400">Right or middle drag</span>
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'move' as const, label: 'Move', icon: FaArrowsAlt },
                        { id: 'rotate' as const, label: 'Rotate', icon: FaSyncAlt },
                        { id: 'scale' as const, label: 'Scale', icon: FaCube },
                      ].map((item) => {
                        const Icon = item.icon;
                        const isActive = activeTransformMode === item.id;

                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => setActiveTransformMode(item.id)}
                            className={`inline-flex min-h-9 items-center justify-center gap-1.5 rounded-md border px-2 text-xs font-bold transition ${
                              isActive
                                ? 'border-red-300/60 bg-red-500 text-white shadow-lg shadow-red-500/25'
                                : 'border-white/10 bg-white/5 text-gray-300 hover:border-red-300/40 hover:bg-red-500/15'
                            }`}
                          >
                            <Icon className="text-sm" />
                            {item.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="grid grid-cols-3 gap-1.5 text-center text-[11px] sm:text-xs">
                      <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1.5">
                        <span className="block text-gray-500">Move</span>
                        <span className="font-semibold text-white">{Math.round(moveX)}px, {Math.round(moveY)}px</span>
                      </div>
                      <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1.5">
                        <span className="block text-gray-500">Rotate</span>
                        <span className="font-semibold text-white">{rotation}deg</span>
                      </div>
                      <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1.5">
                        <span className="block text-gray-500">Scale</span>
                        <span className="font-semibold text-white">{modelScale.toFixed(1)}x</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                  <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2">
                    <div className="mb-1.5 flex items-center justify-between gap-3 text-xs">
                      <label htmlFor="sun-rotation" className="inline-flex items-center gap-2 font-semibold text-gray-200">
                        <FaSun className={isNightMode ? 'text-cyan-200' : 'text-yellow-300'} />
                        Sun Rotate
                      </label>
                      <span className="text-gray-400">{sunRotation}deg</span>
                    </div>
                    <input
                      id="sun-rotation"
                      type="range"
                      min="0"
                      max="360"
                      step="5"
                      value={sunRotation}
                      onChange={(event) => setSunRotation(Number(event.target.value))}
                      className="h-2 w-full cursor-pointer accent-red-500"
                    />
                  </div>

                  <p className="text-[11px] leading-4 text-cyan-100/80 md:max-w-xs">
                    {viewerMode === 'viewer'
                      ? '3D viewer mode keeps the full model area free for desktop mouse controls.'
                      : `Drag model area to ${activeTransformMode}. Pinch with two fingers to scale.`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="absolute bottom-5 left-1/2 z-20 -translate-x-1/2 rounded-full border border-white/10 bg-black/65 px-4 py-2 text-sm font-semibold text-gray-200 backdrop-blur-xl">
                Model loading...
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default WebAR;
